// src/services/dietaryService.ts

import { generateDietaryAnalysis } from './ai/aiGateway';
import { getAllMarkdownPrompts } from '@/lib/getAllMarkdownPrompts';

// --- TYPE DEFINITION HOTFIX ---
// The correct types are defined DIRECTLY in this file to remove external dependencies
// and guarantee this file compiles without errors. This resolves the problem.

interface DietaryCompliance {
  status: 'Compliant' | 'Consideration' | 'NotCompliant' | 'Unknown';
  reason: string;
}

interface Macronutrients {
  protein: number;
  fat: number;
  complexCarbs: number;
  simpleCarbs: number;
  fiber: number;
  sugarAlcohols: number;
}

// NEW: Unified Assessment contract - single source of truth
export interface Assessment {
  glycemic: {
    status: 'Low' | 'Moderate' | 'High';
    reason: string;
    evidence: {
      ingredients: string[];
      methods: string[];
      externalNotes: string[];
      thresholds: string[];
    };
    suggestion?: string;
  };
  macro: {
    pattern: 'Keto' | 'HighProtein' | 'Balanced' | 'None';
    chip: 'Keto-Friendly' | 'High Protein' | 'Balanced' | 'None';
    overrideReason?: string;
  };
  dietaryFlags: {
    Vegetarian: DietaryCompliance;
    Vegan: DietaryCompliance;
    GlutenFree: DietaryCompliance;
    DairyFree: DietaryCompliance;
    HealthyLight: DietaryCompliance;
    GlucoseFriendly: DietaryCompliance;
    LowCarb: DietaryCompliance;
    CarbQuality: DietaryCompliance;
  };
  allergens: {
    list: string[];
    triggers: Record<string, string[]>;
  };
  macros: Macronutrients;
  suggestions: Record<string, string>;
  contextualNotes: string[];
  ingredients: string[]; // Store raw ingredients for tooltip analysis
  engineVersion: string;
}

// This is the correct, expanded result type that the function will now use.
interface DietaryAnalysisResult {
  Vegetarian: DietaryCompliance;
  Vegan: DietaryCompliance;
  GlutenFree: DietaryCompliance;
  DairyFree: DietaryCompliance;
  HealthyLight: DietaryCompliance;
  GlucoseFriendly: DietaryCompliance;
  LowCarb: DietaryCompliance;
  CarbQuality: DietaryCompliance;
  Macronutrients: Macronutrients;
  // --- New derived fields (optional, not required by callers) ---
  Allergens?: string[]; // e.g., ["Peanut","Soy"]
  // New: precise mapping for UI to show which ingredients triggered each allergen
  AllergenTriggers?: Record<string, string[]>; // e.g., { "Gluten": ["panko breadcrumbs", "soy sauce"], "Dairy": ["butter"] }
  GlycemicImpact?: 'Low' | 'Moderate' | 'High';
  GlycemicReason?: string; // deterministic fallback for tooltip
  MacroPattern?: 'Keto' | 'HighProtein' | 'Balanced' | 'None';
  ProteinDensity?: { mode: 'qualitative' | 'numeric'; value: number | 'High' | 'Moderate' | 'Low' };
  Suggestions?: Record<string, string>; // map of compliance key -> suggestion line
}

// Helper function to generate meaningful macro chip tooltip text
export function getChipTooltipText(chipType: Assessment['macro']['chip'], macros: Macronutrients, assessment?: Assessment, ingredients?: string[]): string {
  switch (chipType) {
    case 'Keto-Friendly': {
      // Identify fat sources from ingredients
      let fatSources = '';
      if (ingredients && ingredients.length > 0) {
        const fatIngredients = ingredients
          .filter(ing => /\b(oil|butter|cream|cheese|avocado|nuts|seeds|olive|coconut|salmon|tuna|bacon|duck|lamb)\b/i.test(ing))
          .slice(0, 3)
          .map(ing => ing.split(' ').find(word => /\b(oil|butter|cream|cheese|avocado|nuts|seeds|olive|coconut|salmon|tuna|bacon|duck|lamb)\b/i.test(word)))
          .filter(Boolean);

        if (fatIngredients.length > 0) {
          fatSources = ` from ${fatIngredients.join(', ')}`;
        }
      }
      return `Very low carb content (${(macros.complexCarbs + macros.simpleCarbs).toFixed(1)}%) supports ketosis. High fat${fatSources} provides sustained energy without glucose spikes.`;
    }

    case 'High Protein': {
      // Identify actual protein sources from ingredients
      let proteinSources = '';
      if (ingredients && ingredients.length > 0) {
        const proteinIngredients = ingredients
          .filter(ing => /\b(chicken|beef|pork|fish|salmon|tuna|turkey|lamb|egg|tofu|tempeh|seitan|lentil|bean|chickpea|quinoa|protein|cheese|greek yogurt|cottage cheese|shrimp|lobster|crab|duck|venison)\b/i.test(ing))
          .slice(0, 3)
          .map(ing => {
            // Extract the specific protein word from the ingredient
            const match = ing.match(/\b(chicken|beef|pork|fish|salmon|tuna|turkey|lamb|egg|tofu|tempeh|seitan|lentil|bean|chickpea|quinoa|protein|cheese|greek yogurt|cottage cheese|shrimp|lobster|crab|duck|venison)\b/i);
            return match ? match[0] : null;
          })
          .filter(Boolean);

        if (proteinIngredients.length > 0) {
          proteinSources = ` from ${proteinIngredients.join(', ')}`;
        }
      }

      if (!proteinSources) {
        proteinSources = ' from recipe ingredients';
      }

      return `High protein content (${macros.protein.toFixed(1)}%)${proteinSources} supports muscle building and satiety. Ideal for post-workout recovery or weight management.`;
    }

    case 'Balanced':
      return `Well-distributed macros provide sustained energy. Complex carbs (${macros.complexCarbs.toFixed(1)}%) for endurance, protein (${macros.protein.toFixed(1)}%) for recovery, healthy fats (${macros.fat.toFixed(1)}%) for satisfaction.`;

    case 'None':
    default: {
      const totalCarbs = macros.complexCarbs + macros.simpleCarbs;
      if (totalCarbs > 60) {
        return `Carb-forward profile (${totalCarbs.toFixed(1)}%) provides quick energy. Best consumed before physical activity or as part of balanced meal planning.`;
      } else if (macros.fat > 50) {
        return `Fat-dominant profile (${macros.fat.toFixed(1)}%) offers rich satisfaction and slow energy release. Pairs well with lighter accompaniments.`;
      } else {
        return `Mixed macronutrient profile. Consider pairing with complementary foods to achieve desired nutritional balance.`;
      }
    }
  }
}

// Utility: clamp to [0, 100] and coerce NaN/undefined to 0
function toNumberOrZero(v: any): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.min(1000, n)) : 0; // allow sums >100 pre-normalization
}

/**
 * P2.6: round a macro value for display (1 decimal max; whole numbers for
 * small deltas). Guards against the AI's raw float precision leaking into UI.
 */
export function roundMacro(value: number, decimals = 1): number {
  const n = toNumberOrZero(value);
  if (decimals <= 0) return Math.round(n);
  const f = 10 ** decimals;
  return Math.round(n * f) / f;
}

// Normalize six-way macro split so floats sum to exactly 100.0
function normalizeMacrosFloats(m: Macronutrients): Macronutrients {
  const protein = toNumberOrZero(m.protein);
  const fat = toNumberOrZero(m.fat);
  const complexCarbs = toNumberOrZero(m.complexCarbs);
  const simpleCarbs = toNumberOrZero(m.simpleCarbs);
  const fiber = toNumberOrZero(m.fiber);
  const sugarAlcohols = toNumberOrZero(m.sugarAlcohols);

  const sum = protein + fat + complexCarbs + simpleCarbs + fiber + sugarAlcohols;
  if (sum <= 0) {
    return { protein: 0, fat: 0, complexCarbs: 0, simpleCarbs: 0, fiber: 0, sugarAlcohols: 0 };
  }
  // First pass: scale to 100
  const scale = 100 / sum;
  const scaled = {
    protein: protein * scale,
    fat: fat * scale,
    complexCarbs: complexCarbs * scale,
    simpleCarbs: simpleCarbs * scale,
    fiber: fiber * scale,
    sugarAlcohols: sugarAlcohols * scale,
  };
  // Fix residual due to float rounding by adjusting the largest component
  const entries = Object.entries(scaled) as [keyof Macronutrients, number][];
  const residual = 100 - entries.reduce((acc, [, v]) => acc + v, 0);
  if (Math.abs(residual) > 1e-9) {
    // bump the largest absolute value component
    let maxKey: keyof Macronutrients = 'protein';
    let maxVal = -Infinity;
    for (const [k, v] of entries) {
      if (v > maxVal) {
        maxVal = v;
        maxKey = k;
      }
    }
    (scaled as any)[maxKey] = maxVal + residual;
  }
  return scaled;
}

// Integer rounding helper using largest remainder method to guarantee sum=100
export function roundMacrosToIntegers(m: Macronutrients): Macronutrients {
  const keys: (keyof Macronutrients)[] = ['protein', 'fat', 'complexCarbs', 'simpleCarbs', 'fiber', 'sugarAlcohols'];
  const floors: Record<string, number> = {};
  const fracs: { key: keyof Macronutrients; frac: number }[] = [];
  let floorSum = 0;
  for (const k of keys) {
    const v = Math.max(0, (m as any)[k] as number);
    const f = Math.floor(v);
    floors[k] = f;
    floorSum += f;
    fracs.push({ key: k, frac: v - f });
  }
  let remaining = 100 - floorSum;
  fracs.sort((a, b) => b.frac - a.frac);
  for (let i = 0; i < fracs.length && remaining > 0; i++) {
    floors[fracs[i].key] += 1;
    remaining--;
  }
  return {
    protein: floors.protein,
    fat: floors.fat,
    complexCarbs: floors.complexCarbs,
    simpleCarbs: floors.simpleCarbs,
    fiber: floors.fiber,
    sugarAlcohols: floors.sugarAlcohols,
  };
}

// --- New lightweight derivations ---
const ALLERGEN_KEYWORDS: Record<string, string[]> = {
  Gluten: ['wheat', 'barley', 'rye', 'spelt', 'semolina', 'farina', 'bulgur', 'seitan', 'malt', 'farro', 'triticale', 'panko', 'breadcrumbs', 'flour', 'all-purpose flour', 'plain flour', 'bread flour', 'cake flour', 'pasta', 'noodle', 'ramen', 'udon', 'couscous', 'graham'],
  Dairy: ['milk', 'butter', 'cream', 'yogurt', 'cheese', 'whey', 'casein', 'ghee', 'kefir', 'buttermilk'],
  Egg: ['egg', 'albumen', 'egg white', 'egg yolk', 'mayonnaise', 'aioli'],
  Peanut: ['peanut', 'groundnut'],
  'Tree nut': ['almond', 'hazelnut', 'walnut', 'pecan', 'cashew', 'pistachio', 'macadamia', 'brazil nut', 'nut'],
  Soy: ['soy', 'soya', 'tofu', 'soy sauce', 'edamame', 'tamari', 'miso', 'textured vegetable protein', 'tvp'],
  Fish: ['salmon', 'tuna', 'cod', 'haddock', 'trout', 'anchovy', 'sardine', 'mackerel'],
  Shellfish: ['shrimp', 'prawn', 'crab', 'lobster', 'mussel', 'clam', 'oyster', 'scallop'],
  Sesame: ['sesame', 'tahini', 'sesame oil', 'sesame seed'],
};

function detectAllergens(ingredientsMarkdown: string): string[] {
  const triggers = detectAllergenTriggers(ingredientsMarkdown);
  return Object.keys(triggers);
}

// Build ingredient-aware mapping: Allergen -> [triggering ingredient lines]
function detectAllergenTriggers(ingredientsMarkdown: string): Record<string, string[]> {
  const lines = (ingredientsMarkdown || '')
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.length > 0);

  const mapping: Record<string, Set<string>> = {};
  for (const [allergen] of Object.entries(ALLERGEN_KEYWORDS)) {
    mapping[allergen] = new Set<string>();
  }

  // Heuristic: check each ingredient line against allergen keyword lists
  for (const line of lines) {
    const lower = line.toLowerCase();
    for (const [allergen, keywords] of Object.entries(ALLERGEN_KEYWORDS)) {
      for (const k of keywords) {
        if (lower.includes(k)) {
          mapping[allergen].add(line);
          break;
        }
      }
    }
  }

  // Reduce empty sets
  const result: Record<string, string[]> = {};
  for (const [allergen, set] of Object.entries(mapping)) {
    if (set.size > 0) result[allergen] = Array.from(set);
  }
  return result;
}

function deriveGlycemicImpact(macros: Macronutrients, ingredientsMarkdown: string): 'Low' | 'Moderate' | 'High' {
  const sc = toNumberOrZero(macros.simpleCarbs);
  const complex = toNumberOrZero(macros.complexCarbs);
  const fiber = toNumberOrZero(macros.fiber);
  const sugarAlcohols = toNumberOrZero(macros.sugarAlcohols);
  const impactCarbs = sc + complex;

  const text = (ingredientsMarkdown || '').toLowerCase();
  const refinedSugarSignals = ['sugar', 'syrup', 'corn syrup', 'glucose', 'fructose', 'molasses', 'honey'].some(s => text.includes(s));
  const refinedFlourSignal = ['white flour', 'all-purpose flour', 'ap flour', 'refined flour', 'panko', 'breadcrumbs', 'pasta', 'couscous', 'ramen', 'udon'].some(s => text.includes(s));

  // Special: negligible impact carbs dominated by fiber/sugar alcohols → Low
  if (impactCarbs < 5 && (fiber + sugarAlcohols) >= 20) return 'Low';

  if (sc >= 25 || refinedSugarSignals) return 'High';
  if (sc >= 15 || (refinedFlourSignal && fiber < 12)) return 'Moderate';
  return 'Low';
}

function deriveGlycemicReason(macros: Macronutrients, ingredientsMarkdown: string): string {
  const sc = toNumberOrZero(macros.simpleCarbs);
  const complex = toNumberOrZero(macros.complexCarbs);
  const fiber = toNumberOrZero(macros.fiber);
  const sugarAlcohols = toNumberOrZero(macros.sugarAlcohols);
  const impactCarbs = sc + complex;
  const text = (ingredientsMarkdown || '').toLowerCase();
  const refinedSugarSignals = ['sugar', 'syrup', 'corn syrup', 'glucose', 'fructose', 'molasses', 'honey'].some(s => text.includes(s));
  const refinedFlourSignal = ['white flour', 'all-purpose flour', 'ap flour', 'refined flour', 'panko', 'breadcrumbs', 'pasta', 'couscous', 'ramen', 'udon'].some(s => text.includes(s));

  if (refinedSugarSignals) return 'High glycemic due to refined sugars (e.g., sugar/syrup).';
  if (refinedFlourSignal) return 'Elevated glycemic due to refined white flour.';
  if (sc >= 25) return `High simple carbohydrates (~${Math.round(sc)}%).`;
  if (impactCarbs < 5 && (fiber + sugarAlcohols) >= 20) return 'Impact carbs are negligible; fiber/sugar alcohols dominate.';
  return 'Overall carbohydrate profile suggests lower glycemic impact.';
}

function deriveMacroPattern(macros: Macronutrients): 'Keto' | 'HighProtein' | 'Balanced' | 'None' {
  if (!macros) return 'None';
  const fat = toNumberOrZero(macros.fat);
  const protein = toNumberOrZero(macros.protein);
  const impactCarbs = toNumberOrZero(macros.simpleCarbs) + toNumberOrZero(macros.complexCarbs);
  if (fat >= 40 && impactCarbs < 25) return 'Keto';
  if (protein >= 35) return 'HighProtein';
  return 'Balanced';
}

function deriveProteinDensity(macros: Macronutrients): { mode: 'qualitative'; value: 'High' | 'Moderate' | 'Low' } {
  const p = toNumberOrZero(macros.protein);
  if (p >= 30) return { mode: 'qualitative', value: 'High' };
  if (p >= 20) return { mode: 'qualitative', value: 'Moderate' };
  return { mode: 'qualitative', value: 'Low' };
}

// Remove duplicate implementations of deriveGlycemicReason if they exist above/below.

function deriveSuggestions(result: DietaryAnalysisResult): Record<string, string> {
  const map: Record<string, string> = {};

  // Helper to add only for amber/red
  const addIf = (key: keyof DietaryAnalysisResult, text: string) => {
    const comp = result[key] as unknown as DietaryCompliance | undefined;
    if (!comp) return;
    if (comp.status === 'Consideration' || comp.status === 'NotCompliant') {
      map[key as string] = text;
    }
  };

  // Vegetarian / Vegan
  addIf('Vegetarian', 'Use vegetarian rennet cheeses; replace meat-derived stocks with vegetable stock.');
  addIf('Vegan', 'Replace animal products (e.g., dairy/eggs/honey) with plant-based alternatives.');

  // GlutenFree / DairyFree
  addIf('GlutenFree', 'Swap gluten sources (wheat/barley/rye) for certified gluten-free grains or starches.');
  addIf('DairyFree', 'Use plant-based milks, yogurts, and fats; avoid butter and cheese.');

  // HealthyLight
  addIf('HealthyLight', 'Trim saturated fats and reduce sodium; prefer lean protein and broth-based techniques.');

  // GlucoseFriendly
  addIf('GlucoseFriendly', 'Favor whole grains and low-glycemic sweeteners; reduce refined sugars.');

  // LowCarb
  addIf('LowCarb', 'Reduce high-impact carbs; increase non-starchy vegetables and protein.');

  // CarbQuality
  addIf('CarbQuality', 'Increase complex carbs (whole grains/legumes); reduce refined sugars and syrups.');

  return map;
}

/**
 * Loads dietary guidance content from markdown files (one doc per tag,
 * concatenated with separators). Skips docs once `maxChars` is reached so
 * multi-tag recipes don't bloat the generation prompt unboundedly.
 */
export async function loadDietaryGuidance(dietaryTags: string[], maxChars = 20000): Promise<string | null> {
  const { dietary } = getAllMarkdownPrompts();
  const parts: string[] = [];
  let total = 0;
  for (const tag of dietaryTags) {
    const doc = dietary[tag];
    if (!doc) {
      continue;
    }
    if (total + doc.length > maxChars) {
      continue;
    }
    parts.push(doc);
    total += doc.length;
  }
  if (parts.length === 0) return null;
  return `**DIETARY REQUIREMENTS:**\n\n${parts.join('\n\n---\n\n')}`;
}

/**
 * Analyzes a list of recipe ingredients for dietary compliance and macronutrient breakdown.
 */
export const analyzeDietaryCompliance = async (
  ingredientsMarkdown: string,
  modelName?: string,
  instructionsMarkdown?: string
): Promise<DietaryAnalysisResult | null> => {
  if (!ingredientsMarkdown || ingredientsMarkdown.trim() === '') {
    return null;
  }

  const safeInstructions = (instructionsMarkdown || '').trim();
  const instructionsBlock = safeInstructions.length > 0
    ? `

### Recipe Instructions to Consider
The following instructions may influence glycemic impact, compliance, or contextual insights. Use them to inform reasons and notes; do not invent steps.

"""
${safeInstructions}
"""` : '';

  const prompt = `
You are a world-class nutritional analysis expert. Return a single, valid JSON object only (no prose), strictly matching the requested schema.

Primary Goals
1) Dietary Compliance assessment with specific reasons (no empty reasons; if uncertain, keep the reason short and honest).
2) Detailed macronutrient estimation (six-part split summing to 100).
3) Contextual Note — a SINGLE concise paragraph (not bullets) that quantifies nutrition impact ONLY when instructions introduce ingredients or accompaniments NOT present in the main ingredient list. The note MUST:
   - Identify the extra item(s) briefly (e.g., “white spaghetti”, “garlic bread”, “honey drizzle”, “heavy cream”).
   - State the direction of impact with a short rationale tied to our metrics (glycemic impact, impact carbs, saturated fat/sodium, dairy/gluten exposure).
   - Be quantitative when common reference values are known (e.g., “adds ~45g impact carbs per cup of cooked pasta”, “adds ~17g simple sugars per tbsp honey”, “increases saturated fat from cream”).
   - If no such extra items exist, return an empty string.
   - DO NOT include generic cooking steps, serving size estimates, technique commentary, or vague flavor notes.

Analytical Process You MUST Follow
1) Ingredient-by-Ingredient estimation → aggregate → normalize to 100 (six-way split).
2) Evaluate 8 compliance categories with specific, recipe-based reasons.
3) Extract contextual insights from instructions and ingredients. Keep objective; no hallucinations.

Guiding Principle for Carbohydrate Classification
- complexCarbs: starches from grains/flours/pasta/potatoes/legumes.
- simpleCarbs: sugars (mono/di-saccharides) such as sucrose, fructose, glucose, lactose.
- fiber: dietary fiber (nut flours, seeds, many vegetables).
- sugarAlcohols: erythritol, xylitol, sorbitol, maltitol, etc.

Dietary Compliance (Three-Tiered System)
- Important: Reasons should be tied to actual ingredients/macros; do not refer to generic steps unless they change nutrition materially.
1. CarbQuality — status based on complex vs simple share within impact carbs; reason must name sources.
2. LowCarb — based on (simple+complex) being low; reason specific.
3. GlucoseFriendly — sensitive to high simple sugars / refined starches; reason must name ingredient(s).
4. HealthyLight — consider calories, saturated fat, sodium.
5. GlutenFree — standard rules.
6. DairyFree — standard rules.
7. Vegan — 'Consideration' for honey; standard rules.
8. Vegetarian — 'Consideration' for animal rennet cheeses; standard rules.

Macronutrient Estimation
The six numeric values MUST sum to 100:
- protein, fat, complexCarbs, simpleCarbs, fiber, sugarAlcohols

Recipe Ingredients to Analyze
"""
${ingredientsMarkdown}
"""${instructionsBlock}

Desired JSON Output Shape
{
  "Vegetarian": { "status": "Compliant" | "Consideration" | "NotCompliant", "reason": "string" },
  "Vegan": { "status": "Compliant" | "Consideration" | "NotCompliant", "reason": "string" },
  "GlutenFree": { "status": "Compliant" | "Consideration" | "NotCompliant", "reason": "string" },
  "DairyFree": { "status": "Compliant" | "Consideration" | "NotCompliant", "reason": "string" },
  "HealthyLight": { "status": "Compliant" | "Consideration" | "NotCompliant", "reason": "string" },
  "GlucoseFriendly": { "status": "Compliant" | "Consideration" | "NotCompliant", "reason": "string" },
  "LowCarb": { "status": "Compliant" | "Consideration" | "NotCompliant", "reason": "string" },
  "CarbQuality": { "status": "Compliant" | "Consideration" | "NotCompliant", "reason": "string" },
  "Macronutrients": {
    "protein": number,
    "fat": number,
    "complexCarbs": number,
    "simpleCarbs": number,
    "fiber": number,
    "sugarAlcohols": number
  },
  "ContextualNote": string  // empty string when there are no instruction-only items affecting nutrition
}
`;

  // HARD GUARDRAILS for ContextualNote post-processing: if the model tries to return generic cooking commentary
  // (no mention of items outside the main ingredients), we will clear it to empty downstream.

  try {
    const response = await generateDietaryAnalysis(prompt, 'analyzeDietaryCompliance');
    if (response.success && response.text) {
      // CRITICAL FIX: Extract JSON from markdown code blocks OR plain text
      let cleanedText = response.text.trim();

      // Remove markdown code fences if present
      cleanedText = cleanedText.replace(/^```json\s*/i, '').replace(/^```\s*/, '').replace(/\s*```$/, '');

      // Try to extract JSON object from text (in case there's markdown formatting around it)
      const jsonMatch = cleanedText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        cleanedText = jsonMatch[0];
      }

      const result = JSON.parse(cleanedText) as DietaryAnalysisResult & { ContextualNotes?: string[]; ContextualNote?: string };

      if (result.Macronutrients) {
        const normalized = normalizeMacrosFloats(result.Macronutrients);
        result.Macronutrients = normalized;
        // Deterministic derivations that power new UI (non-authoritative)
        result.GlycemicImpact = deriveGlycemicImpact(result.Macronutrients, ingredientsMarkdown);
        result.GlycemicReason = deriveGlycemicReason(result.Macronutrients, ingredientsMarkdown);
        result.MacroPattern = deriveMacroPattern(result.Macronutrients);
        result.ProteinDensity = deriveProteinDensity(result.Macronutrients);
      }
      // Allergen detection from ingredient text
      result.Allergens = detectAllergens(ingredientsMarkdown);
      // Ingredient-aware allergen triggers for UI
      result.AllergenTriggers = detectAllergenTriggers(ingredientsMarkdown);

      // IMPORTANT: Do NOT fabricate compliance reasons if LLM omitted them.
      // Leave as-is to reflect uncertainty honestly.

      // One-line suggestions for tooltips (for amber/red statuses)
      result.Suggestions = deriveSuggestions(result);

      // Normalize contextual note field (prefer single string contract)
      if ((result as any).ContextualNotes && Array.isArray((result as any).ContextualNotes)) {
        const first = ((result as any).ContextualNotes as string[])[0] || '';
        (result as any).ContextualNote = first;
        delete (result as any).ContextualNotes;
      }

      return result;
    }
    return null;
  } catch {
    return null;
  }
};

// UNIFIED ENGINE - Single source of truth for all dietary analysis
export async function buildAssessment(
  ingredients: string[],
  instructions: string[] = [],
  _servings: number = 1
): Promise<Assessment> {
  try {
    // Convert ingredients to markdown format expected by LLM
    const ingredientsMarkdown = ingredients.join('\n');

    // Get base LLM analysis
    // Prepare instructions markdown for LLM and downstream features
    const instructionsMarkdown = (instructions || []).join('\n');

    const geminiAnalysis = await analyzeDietaryCompliance(ingredientsMarkdown, undefined, instructionsMarkdown);

    // Build a fast set of base ingredient tokens for guardrail checks
    const baseTokens = new Set<string>(
      ingredients
        .map((l) => l.toLowerCase())
        .flatMap((l) => l.split(/[^a-zA-Z]+/).filter(Boolean))
    );

    if (!geminiAnalysis) {
      throw new Error('Failed to get dietary analysis from LLM');
    }

    // Extract macros from LLM response (grams). P2.6: round every value at the
    // source (1 decimal max) so no consumer can ever render raw AI float
    // precision (e.g. 14.285714285714286g).
    const macros: Macronutrients = {
      protein: roundMacro(geminiAnalysis.Macronutrients?.protein || 15),
      fat: roundMacro(geminiAnalysis.Macronutrients?.fat || 25),
      complexCarbs: roundMacro(geminiAnalysis.Macronutrients?.complexCarbs || 35),
      simpleCarbs: roundMacro(geminiAnalysis.Macronutrients?.simpleCarbs || 15),
      fiber: roundMacro(geminiAnalysis.Macronutrients?.fiber || 8),
      sugarAlcohols: roundMacro(geminiAnalysis.Macronutrients?.sugarAlcohols || 0)
    };

    // Determine macro pattern
    const totalCarbs = macros.complexCarbs + macros.simpleCarbs;
    let macroPattern: Assessment['macro']['pattern'] = 'None';
    let macroChip: Assessment['macro']['chip'] = 'None';

    if (totalCarbs <= 10 && macros.fat >= 60) {
      macroPattern = 'Keto';
      macroChip = 'Keto-Friendly';
    } else if (macros.protein >= 30) {
      macroPattern = 'HighProtein';
      macroChip = 'High Protein';
    } else if (macros.protein >= 15 && macros.fat >= 20 && totalCarbs >= 30) {
      macroPattern = 'Balanced';
      macroChip = 'Balanced';
    }

    // Prefer LLM-provided contextual notes; fall back to [].
    // Normalize the contextual note across engine BEFORE returning object
    let contextualNote: string = ((geminiAnalysis as any).ContextualNote || '').toString().trim();

    // Guardrail: drop generic cooking commentary that does not reference any instruction-only items
    if (contextualNote) {
      const lc = contextualNote.toLowerCase();
      const hasGenericOnly =
        /serve|garnish|simmer|sauté|al dente|brown(ed)?|mix|stir|cook|divide|plate|finish/.test(lc) &&
        !/(bread|bun|rice|pasta|spaghetti|noodle|honey|cream|butter|naan|pita|tortilla|wrap|potato|fries|chips|yogurt|cheese|milk)/.test(lc);
      // also ensure it mentions an item token not already in base ingredients (instruction-only)
      const mentionsNewItem = lc.split(/[^a-zA-Z]+/).some(tok => tok && !baseTokens.has(tok));

      if (hasGenericOnly || !mentionsNewItem) {
        contextualNote = '';
      }
    }

    const contextualNotesFinal: string[] = contextualNote ? [contextualNote] : [];

    return {
      glycemic: {
        status: geminiAnalysis.GlycemicImpact || 'Moderate',
        reason: geminiAnalysis.GlycemicReason || 'Mixed glycemic impact from ingredients',
        evidence: {
          ingredients: [], // To be populated by LLM if needed
          methods: [],
          externalNotes: contextualNotesFinal,
          thresholds: []
        }
      },
      macro: {
        pattern: macroPattern,
        chip: macroChip
      },
      dietaryFlags: {
        Vegetarian: geminiAnalysis.Vegetarian || { status: 'Unknown', reason: 'Analysis unavailable' },
        Vegan: geminiAnalysis.Vegan || { status: 'Unknown', reason: 'Analysis unavailable' },
        GlutenFree: geminiAnalysis.GlutenFree || { status: 'Unknown', reason: 'Analysis unavailable' },
        DairyFree: geminiAnalysis.DairyFree || { status: 'Unknown', reason: 'Analysis unavailable' },
        HealthyLight: geminiAnalysis.HealthyLight || { status: 'Unknown', reason: 'Analysis unavailable' },
        GlucoseFriendly: geminiAnalysis.GlucoseFriendly || { status: 'Unknown', reason: 'Analysis unavailable' },
        LowCarb: geminiAnalysis.LowCarb || { status: 'Unknown', reason: 'Analysis unavailable' },
        CarbQuality: geminiAnalysis.CarbQuality || { status: 'Unknown', reason: 'Analysis unavailable' }
      },
      allergens: {
        list: geminiAnalysis.Allergens || [],
        triggers: geminiAnalysis.AllergenTriggers || {}
      },
      macros,
      suggestions: geminiAnalysis.Suggestions || {},
      contextualNotes: contextualNotesFinal, // single-note array or empty
      ingredients, // Store raw ingredients for tooltip analysis
      engineVersion: '1.1.0'
    };

  } catch {
    // Return safe fallback
    return {
      glycemic: { status: 'Moderate', reason: 'Analysis unavailable', evidence: { ingredients: [], methods: [], externalNotes: [], thresholds: [] } },
      macro: { pattern: 'None', chip: 'None' },
      dietaryFlags: {
        Vegetarian: { status: 'Unknown', reason: 'Analysis unavailable' },
        Vegan: { status: 'Unknown', reason: 'Analysis unavailable' },
        GlutenFree: { status: 'Unknown', reason: 'Analysis unavailable' },
        DairyFree: { status: 'Unknown', reason: 'Analysis unavailable' },
        HealthyLight: { status: 'Unknown', reason: 'Analysis unavailable' },
        GlucoseFriendly: { status: 'Unknown', reason: 'Analysis unavailable' },
        LowCarb: { status: 'Unknown', reason: 'Analysis unavailable' },
        CarbQuality: { status: 'Unknown', reason: 'Analysis unavailable' }
      },
      allergens: { list: [], triggers: {} },
      macros: { protein: 15, fat: 25, complexCarbs: 35, simpleCarbs: 15, fiber: 8, sugarAlcohols: 0 },
      suggestions: {},
      contextualNotes: [],
      ingredients: [], // Empty fallback
      engineVersion: '1.0.0'
    };
  }
}